import { useEffect, useState } from "react";
import { Header } from "@/components/brand/Header";
import { Footer } from "@/components/brand/Footer";
import { MarketTicker } from "@/components/brand/MarketTicker";
import { TeamSection } from "@/components/brand/TeamSection";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Globe2, LineChart, BadgeCheck } from "lucide-react";
import { KAFFA } from "@/constants/testIds";
import { useLocale } from "@/contexts/LocaleContext";
import api from "@/lib/api";

export default function LandingPage() {
  const { t, formatPrice, convertPrice, currency } = useLocale();
  const [arabicaIndicator, setArabicaIndicator] = useState(null);

  useEffect(() => {
    api.get("/indicators")
      .then((r) => {
        const arabica = (r.data || []).find((i) => i.code === "ARABICA_NY");
        if (arabica) setArabicaIndicator(arabica);
      })
      .catch(() => {});
  }, []);

  // ARABICA_NY is in USD/bag — convert to user's selected currency for display
  const livePrice = arabicaIndicator
    ? convertPrice(arabicaIndicator.value, "USD", currency)
    : null;
  const liveChange = arabicaIndicator?.change_pct ?? 0;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <MarketTicker />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="mx-auto max-w-[1600px] px-4 sm:px-8 py-16 lg:py-24 grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-7">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-6 flex items-center gap-3">
              <span className="h-px w-12 bg-accent" />
              {t("hero.tagline")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-black text-foreground leading-[0.95]">
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed text-pretty">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                data-testid={KAFFA.ctaLogin}
                className="h-12 px-6 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-all font-mono-data text-xs uppercase tracking-[0.2em]"
              >
                <Link to="/marketplace">{t("hero.cta_explore")} <ArrowRight className="h-3.5 w-3.5 ml-2" /></Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid={KAFFA.ctaRegister}
                className="h-12 px-6 rounded-none border-border hover:bg-secondary font-mono-data text-xs uppercase tracking-[0.2em]"
              >
                <Link to="/register">{t("hero.cta_register")}</Link>
              </Button>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <div className="font-mono-data text-3xl text-accent">3K+</div>
                <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{t("hero.stat_bags")}</div>
              </div>
              <div>
                <div className="font-mono-data text-3xl text-accent">42</div>
                <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{t("hero.stat_coops")}</div>
              </div>
              <div>
                <div className="font-mono-data text-3xl text-accent">18</div>
                <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{t("hero.stat_countries")}</div>
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5 relative">
            <div className="aspect-[4/5] border border-border overflow-hidden relative bg-[#1a0805]">
              <img
                src="https://images.pexels.com/photos/6135326/pexels-photo-6135326.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Café"
                className="absolute inset-0 h-full w-full object-cover opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0c0604]/90 via-[#5e1f0c]/30 to-transparent" />

              {/* Logo */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[58%] flex flex-col items-center pointer-events-none">
                <div className="relative">
                  <div className="absolute inset-0 bg-[#e9b87a]/30 blur-3xl rounded-full scale-110" />
                  <img
                    src="/kaffa-logo.png"
                    alt="Kaffa Exchange"
                    className="relative h-56 w-56 sm:h-72 sm:w-72 object-contain drop-shadow-[0_0_40px_rgba(233,184,122,0.45)]"
                  />
                </div>
                <div className="mt-2 font-mono-data text-[10px] uppercase tracking-[0.4em] text-[#e9b87a]">
                  KAFFA · EXCHANGE
                </div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-6 backdrop-blur-md bg-black/40 border-t border-[#361b12]">
                <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-[#e9b87a] mb-2">{t("hero.live_market")}</div>
                <div className="font-display text-2xl tracking-tight text-white">
                  {t("hero.live_arabica")} · {livePrice != null ? formatPrice(livePrice, currency) : "—"}/{t("common.bags").replace(/s$/, "")}
                </div>
                <div className={`text-xs font-mono-data mt-1 ${liveChange >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {liveChange >= 0 ? "▲" : "▼"} {Math.abs(liveChange).toFixed(2)}% — {t("hero.live_ny_futures")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-x divide-border">
          {[
            { Icon: ShieldCheck, title: t("pillar.verify.title"), desc: t("pillar.verify.desc") },
            { Icon: Globe2, title: t("pillar.export.title"), desc: t("pillar.export.desc") },
            { Icon: LineChart, title: t("pillar.pricing.title"), desc: t("pillar.pricing.desc") },
            { Icon: BadgeCheck, title: t("pillar.trace.title"), desc: t("pillar.trace.desc") },
          ].map(({ Icon, title, desc }, i) => (
            <div key={i} className="p-8 hover:bg-secondary/30 transition-colors">
              <Icon className="h-5 w-5 text-accent mb-4" strokeWidth={1.5} />
              <div className="font-display text-lg tracking-tight">{title}</div>
              <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission & Values */}
      <section className="border-b border-border bg-secondary/30">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-8 py-20 lg:py-28 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-4 flex items-center gap-3">
              <span className="h-px w-12 bg-accent" />
              {t("mission.tagline")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-black leading-[1.05] text-balance">
              {t("mission.title")}
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground mt-6 max-w-md leading-relaxed text-pretty">
              {t("mission.subtitle")}
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border">
            {[
              { title: t("mission.value1_title"), desc: t("mission.value1_desc"), num: "01" },
              { title: t("mission.value2_title"), desc: t("mission.value2_desc"), num: "02" },
              { title: t("mission.value3_title"), desc: t("mission.value3_desc"), num: "03" },
              { title: t("mission.value4_title"), desc: t("mission.value4_desc"), num: "04" },
            ].map((v, i) => (
              <div key={i} className="bg-card p-7 hover-lift hover:bg-secondary/40">
                <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-3">{v.num}</div>
                <div className="font-display text-xl tracking-tight">{v.title}</div>
                <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <TeamSection />

      {/* Tours teaser */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-8 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-4 flex items-center gap-3">
              <span className="h-px w-12 bg-accent" />
              {t("home.experiences_label")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-black leading-[1.05]">
              {t("tours.public_title")}
            </h2>
            <p className="text-base text-muted-foreground mt-5 max-w-xl leading-relaxed">
              {t("tours.public_subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                asChild
                className="h-12 px-6 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-[0.2em]"
              >
                <Link to="/tours">{t("tours.cta")} <ArrowRight className="h-3.5 w-3.5 ml-2" /></Link>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">✦ 7 · 10 · 15 {t("tours.days")}</span>
              <span className="flex items-center gap-1.5">✦ {t("home.airport_pickup")}</span>
              <span className="flex items-center gap-1.5">✦ {t("home.translator")}</span>
              <span className="flex items-center gap-1.5">✦ {t("tours.from_price")} {formatPrice(convertPrice(1000, "USD", currency), currency)} {t("tours.per_person")}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="aspect-[4/5] border border-border overflow-hidden">
              <img src="https://images.unsplash.com/photo-1497935586351-b67a49e012bf?auto=format&fit=crop&w=700&h=900" alt="" className="h-full w-full object-cover" />
            </div>
            <div className="aspect-[4/5] border border-border overflow-hidden mt-10">
              <img src="https://images.pexels.com/photos/6135326/pexels-photo-6135326.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700" alt="" className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Final marketplace CTA */}
      <section className="border-b border-border bg-secondary/20">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-8 py-20 text-center">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-4">
            {t("nav.marketplace")} · {t("lots.active")}
          </div>
          <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl tracking-tighter font-black leading-[1.05] max-w-3xl mx-auto text-balance">
            {t("lots.public_title")}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground mt-5 max-w-2xl mx-auto text-pretty">
            {t("lots.public_subtitle")}
          </p>
          <div className="mt-10 flex justify-center">
            <Button
              asChild
              className="h-14 px-10 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-[0.25em]"
            >
              <Link to="/marketplace">{t("hero.cta_explore")} <ArrowRight className="h-3.5 w-3.5 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
